import { CategoryTree } from '@/components/category-tree';
import { LocationFilterField, resolveNextRadius } from '@/components/location-filter-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { type FilterOptions, type SelectedLocation } from '@/types';
import { BellRing, ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

export interface BoatFilters {
    category: string[];
    condition: 'new' | 'used' | 'all';
    location: SelectedLocation | null;
    /** Raio em km a partir da cidade (0 = somente na cidade); null quando não se aplica. */
    radius: number | null;
    yearRange: [number, number];
    priceRange: [number, number];
    hours: [number, number];
    /** Tamanho em pés (CN-200): null = sem limite. */
    sizeMin: number | null;
    sizeMax: number | null;
    /** Motor (CN-200 item 7). */
    fuelType: string | null;
    engineBrand: string | null;
    /** Conforto (CN-200 item 7). */
    cabin: boolean;
    bathroom: boolean;
    generator: boolean;
    passengersMin: number | null;
}

/** Combustíveis do cadastro (Step3TechnicalSpecs); o valor vai na URL como texto e o banco compara sem acento/caixa. */
export const FUEL_TYPE_OPTIONS = ['Gasolina', 'Diesel', 'Elétrico', 'Híbrido'];

export const PASSENGERS_MIN_OPTIONS = [4, 6, 8, 10];

/** Filtros de motor/conforto vazios (CN-200), para montar e limpar o estado. */
export const EMPTY_BOAT_ATTRIBUTE_FILTERS: Pick<
    BoatFilters,
    'sizeMin' | 'sizeMax' | 'fuelType' | 'engineBrand' | 'cabin' | 'bathroom' | 'generator' | 'passengersMin'
> = {
    sizeMin: null,
    sizeMax: null,
    fuelType: null,
    engineBrand: null,
    cabin: false,
    bathroom: false,
    generator: false,
    passengersMin: null,
};

/** Quantos filtros de motor/conforto/tamanho estão ativos (para o contador "Filtros (n)"). */
export const countBoatAttributeFilters = (filters: BoatFilters): number =>
    [
        filters.sizeMin !== null || filters.sizeMax !== null,
        filters.fuelType !== null,
        filters.engineBrand !== null,
        filters.cabin,
        filters.bathroom,
        filters.generator,
        filters.passengersMin !== null,
    ].filter(Boolean).length;

interface AppliedFilter {
    type: keyof BoatFilters;
    value: string | number;
    display: string;
}

interface FilterSectionProps {
    title: string;
    expanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}

type SectionKey = 'category' | 'condition' | 'location' | 'year' | 'price' | 'size' | 'hours' | 'engine' | 'comfort';
type RangeBound = 'min' | 'max';

const MOBILE_COLLAPSED_SECTIONS: Record<SectionKey, boolean> = {
    category: false,
    condition: false,
    location: false,
    year: false,
    price: false,
    size: false,
    hours: false,
    engine: false,
    comfort: false,
};

const DESKTOP_DEFAULT_SECTIONS: Record<SectionKey, boolean> = {
    category: true,
    condition: true,
    location: false,
    year: false,
    price: false,
    size: false,
    hours: false,
    engine: false,
    comfort: false,
};

const MOBILE_BREAKPOINT = 1024;
const DEFAULT_HOURS_RANGE: [number, number] = [0, 10000];

const getInitialExpandedState = (hasLocation = false) => {
    if (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT) {
        return { ...MOBILE_COLLAPSED_SECTIONS };
    }

    // Com localização escolhida (ex.: página geo), a seção abre para o chip ficar visível.
    return { ...DESKTOP_DEFAULT_SECTIONS, location: DESKTOP_DEFAULT_SECTIONS.location || hasLocation };
};

const clampNumber = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

const normalizeRange = (value: [number, number], min: number, max: number): [number, number] => {
    const start = clampNumber(value[0], min, max);
    const end = clampNumber(value[1], min, max);

    return [Math.min(start, end), Math.max(start, end)];
};

const parseNullableNumber = (value: string): number | null => {
    const trimmed = value.trim();

    if (!trimmed) {
        return null;
    }

    const parsed = Number(trimmed);

    if (!Number.isFinite(parsed)) {
        return null;
    }

    return parsed;
};

const toRangeValue = (value: number[], fallback: [number, number]): [number, number] => {
    if (value.length < 2) {
        return fallback;
    }

    return [value[0] ?? fallback[0], value[1] ?? fallback[1]];
};

const FilterSection: React.FC<FilterSectionProps> = ({ title, expanded, onToggle, children }) => {
    return (
        <section className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                className="flex w-full items-center justify-between py-2 text-left font-medium text-gray-700 transition-colors hover:text-gray-900"
            >
                {title}
                {expanded ? <ChevronDown aria-hidden="true" className="h-4 w-4" /> : <ChevronRight aria-hidden="true" className="h-4 w-4" />}
            </button>

            {expanded && <div className="mt-3 space-y-3">{children}</div>}
        </section>
    );
};

interface BoatFilterSidebarProps {
    filters: BoatFilters;
    onFiltersChange: (filters: BoatFilters) => void;
    /** Abre o modal "Criar alerta" (CN-192) com os filtros atuais; sem o callback o botão não aparece. */
    onCreateAlert?: () => void;
    filterOptions: FilterOptions;
    className?: string;
    /** Oculta a seção "Localização" (ex.: estoque de uma empresa, que tem endereço fixo). */
    hideLocation?: boolean;
    /** Opções e padrão do raio de distância do módulo (config/search.php). */
    radiusOptions?: number[];
    defaultRadius?: number;
    /** Dentro da gaveta do celular (CN-200): sem o botão "Filtros (n)" e com as seções já visíveis, sem o rodapé próprio. */
    alwaysExpanded?: boolean;
    /** Seções a abrir ao montar (ex.: as que a busca inteligente preencheu). */
    initiallyOpenSections?: string[];
}

export const BoatFilterSidebar: React.FC<BoatFilterSidebarProps> = ({
    filters,
    onFiltersChange,
    onCreateAlert,
    filterOptions,
    className = '',
    hideLocation = false,
    radiusOptions = [],
    defaultRadius = 0,
    alwaysExpanded = false,
    initiallyOpenSections = [],
}) => {
    const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>(() => {
        const initial = alwaysExpanded ? { ...DESKTOP_DEFAULT_SECTIONS, location: filters.location !== null } : getInitialExpandedState(filters.location !== null);

        for (const section of initiallyOpenSections) {
            if (section in initial) {
                initial[section as SectionKey] = true;
            }
        }

        return initial;
    });
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(!alwaysExpanded);
    const [engineBrandText, setEngineBrandText] = useState(filters.engineBrand ?? '');
    const [sizeMinText, setSizeMinText] = useState(filters.sizeMin !== null ? String(filters.sizeMin) : '');
    const [sizeMaxText, setSizeMaxText] = useState(filters.sizeMax !== null ? String(filters.sizeMax) : '');

    const yearMin = filterOptions?.yearRange?.min ?? 1990;
    const yearMax = filterOptions?.yearRange?.max ?? new Date().getFullYear();
    const priceMin = filterOptions?.priceRange?.min ?? 0;
    const priceMax = filterOptions?.priceRange?.max ?? 1000000;
    const hoursMin = DEFAULT_HOURS_RANGE[0];
    const hoursMax = DEFAULT_HOURS_RANGE[1];

    const [yearPreview, setYearPreview] = useState<[number, number]>(() => normalizeRange(filters.yearRange, yearMin, yearMax));
    const [pricePreview, setPricePreview] = useState<[number, number]>(() => normalizeRange(filters.priceRange, priceMin, priceMax));
    const [hoursPreview, setHoursPreview] = useState<[number, number]>(() => normalizeRange(filters.hours, hoursMin, hoursMax));

    const [yearMinText, setYearMinText] = useState(String(filters.yearRange[0]));
    const [yearMaxText, setYearMaxText] = useState(String(filters.yearRange[1]));
    const [priceMinText, setPriceMinText] = useState(String(filters.priceRange[0]));
    const [priceMaxText, setPriceMaxText] = useState(String(filters.priceRange[1]));
    const [hoursMinText, setHoursMinText] = useState(String(filters.hours[0]));
    const [hoursMaxText, setHoursMaxText] = useState(String(filters.hours[1]));

    const formatter = useMemo(() => new Intl.NumberFormat('pt-BR'), []);

    const categoryLookup = useMemo(() => {
        const lookup: Record<number, string> = {};

        for (const category of filterOptions.categories) {
            lookup[category.id] = category.name;
        }

        return lookup;
    }, [filterOptions.categories]);

    const hasActiveFilters =
        filters.category.length > 0 ||
        filters.condition !== 'all' ||
        filters.location !== null ||
        filters.yearRange[0] !== yearMin ||
        filters.yearRange[1] !== yearMax ||
        filters.priceRange[0] !== priceMin ||
        filters.priceRange[1] !== priceMax ||
        filters.hours[0] !== hoursMin ||
        filters.hours[1] !== hoursMax ||
        countBoatAttributeFilters(filters) > 0;

    useEffect(() => {
        setEngineBrandText(filters.engineBrand ?? '');
    }, [filters.engineBrand]);

    useEffect(() => {
        setSizeMinText(filters.sizeMin !== null ? String(filters.sizeMin) : '');
        setSizeMaxText(filters.sizeMax !== null ? String(filters.sizeMax) : '');
    }, [filters.sizeMin, filters.sizeMax]);

    useEffect(() => {
        const normalized = normalizeRange(filters.yearRange, yearMin, yearMax);
        setYearPreview(normalized);
        setYearMinText(String(normalized[0]));
        setYearMaxText(String(normalized[1]));
    }, [filters.yearRange, yearMax, yearMin]);

    useEffect(() => {
        const normalized = normalizeRange(filters.priceRange, priceMin, priceMax);
        setPricePreview(normalized);
        setPriceMinText(String(normalized[0]));
        setPriceMaxText(String(normalized[1]));
    }, [filters.priceRange, priceMax, priceMin]);

    useEffect(() => {
        const normalized = normalizeRange(filters.hours, hoursMin, hoursMax);
        setHoursPreview(normalized);
        setHoursMinText(String(normalized[0]));
        setHoursMaxText(String(normalized[1]));
    }, [filters.hours, hoursMax, hoursMin]);

    useEffect(() => {
        if (alwaysExpanded) {
            return;
        }

        const handleResize = () => {
            if (window.innerWidth >= MOBILE_BREAKPOINT) {
                setExpandedSections({ ...DESKTOP_DEFAULT_SECTIONS });
            }
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, [alwaysExpanded]);

    const toggleSection = (section: SectionKey) => {
        setExpandedSections((previous) => ({
            ...previous,
            [section]: !previous[section],
        }));
    };

    const clearAllFilters = () => {
        onFiltersChange({
            category: [],
            condition: 'all',
            location: null,
            radius: null,
            yearRange: [yearMin, yearMax],
            priceRange: [priceMin, priceMax],
            hours: [hoursMin, hoursMax],
            ...EMPTY_BOAT_ATTRIBUTE_FILTERS,
        });
    };

    const commitEngineBrand = () => {
        const next = engineBrandText.trim().slice(0, 60) || null;

        if (next !== filters.engineBrand) {
            onFiltersChange({ ...filters, engineBrand: next });
        }
    };

    const commitSize = () => {
        const parse = (value: string): number | null => {
            const parsed = parseNullableNumber(value);

            return parsed !== null && parsed > 0 ? Math.round(parsed * 10) / 10 : null;
        };

        let nextMin = parse(sizeMinText);
        let nextMax = parse(sizeMaxText);

        if (nextMin !== null && nextMax !== null && nextMin > nextMax) {
            [nextMin, nextMax] = [nextMax, nextMin];
        }

        if (nextMin !== filters.sizeMin || nextMax !== filters.sizeMax) {
            onFiltersChange({ ...filters, sizeMin: nextMin, sizeMax: nextMax });
        }
    };

    const toggleFlag = (key: 'cabin' | 'bathroom' | 'generator') => onFiltersChange({ ...filters, [key]: !filters[key] });

    const appliedFilters = useMemo<AppliedFilter[]>(() => {
        const nextFilters: AppliedFilter[] = [];

        for (const categoryId of filters.category) {
            nextFilters.push({
                type: 'category',
                value: categoryId,
                display: categoryLookup[Number(categoryId)] || `Categoria ${categoryId}`,
            });
        }

        if (filters.condition !== 'all') {
            nextFilters.push({
                type: 'condition',
                value: filters.condition,
                display: filters.condition === 'new' ? 'Novos' : 'Usados',
            });
        }

        if (filters.location) {
            nextFilters.push({
                type: 'location',
                value: filters.location.id,
                display: filters.location.label,
            });
        }

        if (filters.fuelType) nextFilters.push({ type: 'fuelType', value: filters.fuelType, display: filters.fuelType });
        if (filters.engineBrand) nextFilters.push({ type: 'engineBrand', value: filters.engineBrand, display: filters.engineBrand });
        if (filters.cabin) nextFilters.push({ type: 'cabin', value: 1, display: 'Com cabine' });
        if (filters.bathroom) nextFilters.push({ type: 'bathroom', value: 1, display: 'Com banheiro' });
        if (filters.generator) nextFilters.push({ type: 'generator', value: 1, display: 'Com gerador' });
        if (filters.passengersMin) nextFilters.push({ type: 'passengersMin', value: filters.passengersMin, display: `${filters.passengersMin}+ passageiros` });
        if (filters.sizeMin !== null || filters.sizeMax !== null) nextFilters.push({ type: 'sizeMin', value: filters.sizeMin ?? 0, display: 'Tamanho' });

        return nextFilters;
    }, [categoryLookup, filters]);

    const applyYearRange = (range: [number, number], shouldCommit = false) => {
        const nextRange = normalizeRange(range, yearMin, yearMax);
        setYearPreview(nextRange);
        setYearMinText(String(nextRange[0]));
        setYearMaxText(String(nextRange[1]));

        if (shouldCommit) {
            onFiltersChange({ ...filters, yearRange: nextRange });
        }
    };

    const applyPriceRange = (range: [number, number], shouldCommit = false) => {
        const nextRange = normalizeRange(range, priceMin, priceMax);
        setPricePreview(nextRange);
        setPriceMinText(String(nextRange[0]));
        setPriceMaxText(String(nextRange[1]));

        if (shouldCommit) {
            onFiltersChange({ ...filters, priceRange: nextRange });
        }
    };

    const applyHoursRange = (range: [number, number], shouldCommit = false) => {
        const nextRange = normalizeRange(range, hoursMin, hoursMax);
        setHoursPreview(nextRange);
        setHoursMinText(String(nextRange[0]));
        setHoursMaxText(String(nextRange[1]));

        if (shouldCommit) {
            onFiltersChange({ ...filters, hours: nextRange });
        }
    };

    const commitYearBound = (bound: RangeBound) => {
        const rawValue = bound === 'min' ? yearMinText : yearMaxText;
        const parsed = parseNullableNumber(rawValue);
        let [nextMin, nextMax] = yearPreview;

        if (parsed !== null) {
            const value = clampNumber(Math.round(parsed), yearMin, yearMax);

            if (bound === 'min') {
                nextMin = value;
                nextMax = Math.max(value, nextMax);
            } else {
                nextMax = value;
                nextMin = Math.min(nextMin, value);
            }
        }

        applyYearRange([nextMin, nextMax], true);
    };

    const commitPriceBound = (bound: RangeBound) => {
        const rawValue = bound === 'min' ? priceMinText : priceMaxText;
        const parsed = parseNullableNumber(rawValue);
        let [nextMin, nextMax] = pricePreview;

        if (parsed !== null) {
            const value = clampNumber(Math.round(parsed), priceMin, priceMax);

            if (bound === 'min') {
                nextMin = value;
                nextMax = Math.max(value, nextMax);
            } else {
                nextMax = value;
                nextMin = Math.min(nextMin, value);
            }
        }

        applyPriceRange([nextMin, nextMax], true);
    };

    const commitHoursBound = (bound: RangeBound) => {
        const rawValue = bound === 'min' ? hoursMinText : hoursMaxText;
        const parsed = parseNullableNumber(rawValue);
        let [nextMin, nextMax] = hoursPreview;

        if (parsed !== null) {
            const value = clampNumber(Math.round(parsed), hoursMin, hoursMax);

            if (bound === 'min') {
                nextMin = value;
                nextMax = Math.max(value, nextMax);
            } else {
                nextMax = value;
                nextMin = Math.min(nextMin, value);
            }
        }

        applyHoursRange([nextMin, nextMax], true);
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, commit: () => void) => {
        if (event.key !== 'Enter') {
            return;
        }

        event.preventDefault();
        commit();
        event.currentTarget.blur();
    };

    return (
        <div className={cn('rounded-xl bg-white p-3 lg:flex lg:h-full lg:min-h-0 lg:flex-col lg:p-6', className)}>
            {!alwaysExpanded && (
                <button
                    type="button"
                    onClick={() => setIsSidebarCollapsed((previous) => !previous)}
                    className="flex w-full items-center justify-between py-2 text-left font-semibold text-gray-900 transition-colors hover:text-gray-700 lg:hidden"
                >
                    <span>Filtros {appliedFilters.length > 0 && `(${appliedFilters.length})`}</span>
                    {isSidebarCollapsed ? <ChevronDown aria-hidden="true" className="h-5 w-5" /> : <ChevronUp aria-hidden="true" className="h-5 w-5" />}
                </button>
            )}

            <div
                className={cn(
                    'space-y-6',
                    isSidebarCollapsed ? 'hidden lg:flex lg:h-full lg:min-h-0 lg:flex-col' : 'block lg:flex lg:h-full lg:min-h-0 lg:flex-col',
                )}
            >
                <div className="space-y-6 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-y-contain lg:pr-1">
                    <div className="space-y-4">
                        <FilterSection title="Categoria" expanded={expandedSections.category} onToggle={() => toggleSection('category')}>
                            <CategoryTree
                                categories={filterOptions.categories}
                                selectedCategories={filters.category}
                                onSelectionChange={(nextCategories) => {
                                    onFiltersChange({ ...filters, category: nextCategories });
                                }}
                            />
                        </FilterSection>

                        <FilterSection title="Novo/usado" expanded={expandedSections.condition} onToggle={() => toggleSection('condition')}>
                            <div className="grid grid-cols-2 gap-2">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant={filters.condition === 'new' ? 'default' : 'outline'}
                                    aria-pressed={filters.condition === 'new'}
                                    onClick={() =>
                                        onFiltersChange({
                                            ...filters,
                                            condition: filters.condition === 'new' ? 'all' : 'new',
                                        })
                                    }
                                    className="px-2"
                                >
                                    <span className="truncate">Novos</span>
                                </Button>

                                <Button
                                    type="button"
                                    size="sm"
                                    variant={filters.condition === 'used' ? 'default' : 'outline'}
                                    aria-pressed={filters.condition === 'used'}
                                    onClick={() =>
                                        onFiltersChange({
                                            ...filters,
                                            condition: filters.condition === 'used' ? 'all' : 'used',
                                        })
                                    }
                                    className="px-2"
                                >
                                    <span className="truncate">Usados</span>
                                </Button>
                            </div>
                        </FilterSection>

                        {!hideLocation && (
                            <FilterSection title="Localização" expanded={expandedSections.location} onToggle={() => toggleSection('location')}>
                                <LocationFilterField
                                    searchType="embarcacoes"
                                    location={filters.location}
                                    radius={filters.radius}
                                    radiusOptions={radiusOptions}
                                    onLocationChange={(nextLocation) =>
                                        onFiltersChange({
                                            ...filters,
                                            location: nextLocation,
                                            radius: resolveNextRadius(filters.location, filters.radius, nextLocation, defaultRadius),
                                        })
                                    }
                                    onRadiusChange={(nextRadius) => onFiltersChange({ ...filters, radius: nextRadius })}
                                />
                            </FilterSection>
                        )}

                        <FilterSection title="Ano" expanded={expandedSections.year} onToggle={() => toggleSection('year')}>
                            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 tabular-nums">
                                {yearPreview[0]} - {yearPreview[1]}
                            </div>

                            <Slider
                                id="year-range-slider"
                                aria-label="Intervalo de ano"
                                min={yearMin}
                                max={yearMax}
                                step={1}
                                value={yearPreview}
                                onValueChange={(value) => applyYearRange(toRangeValue(value, yearPreview))}
                                onValueCommit={(value) => applyYearRange(toRangeValue(value, yearPreview), true)}
                                className="py-2"
                            />

                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <Label htmlFor="year-min-input" className="text-xs text-gray-600">
                                        Mínimo
                                    </Label>
                                    <Input
                                        id="year-min-input"
                                        name="year_min"
                                        type="number"
                                        autoComplete="off"
                                        inputMode="numeric"
                                        value={yearMinText}
                                        onChange={(event) => setYearMinText(event.target.value)}
                                        onBlur={() => commitYearBound('min')}
                                        onKeyDown={(event) => handleInputKeyDown(event, () => commitYearBound('min'))}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <Label htmlFor="year-max-input" className="text-xs text-gray-600">
                                        Máximo
                                    </Label>
                                    <Input
                                        id="year-max-input"
                                        name="year_max"
                                        type="number"
                                        autoComplete="off"
                                        inputMode="numeric"
                                        value={yearMaxText}
                                        onChange={(event) => setYearMaxText(event.target.value)}
                                        onBlur={() => commitYearBound('max')}
                                        onKeyDown={(event) => handleInputKeyDown(event, () => commitYearBound('max'))}
                                    />
                                </div>
                            </div>
                        </FilterSection>

                        <FilterSection title="Preço" expanded={expandedSections.price} onToggle={() => toggleSection('price')}>
                            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 tabular-nums">
                                R$ {formatter.format(pricePreview[0])} - R$ {formatter.format(pricePreview[1])}
                            </div>

                            <Slider
                                id="price-range-slider"
                                aria-label="Intervalo de preço"
                                min={priceMin}
                                max={priceMax}
                                step={1000}
                                value={pricePreview}
                                onValueChange={(value) => applyPriceRange(toRangeValue(value, pricePreview))}
                                onValueCommit={(value) => applyPriceRange(toRangeValue(value, pricePreview), true)}
                                className="py-2"
                            />

                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <Label htmlFor="price-min-input" className="text-xs text-gray-600">
                                        Mínimo
                                    </Label>
                                    <Input
                                        id="price-min-input"
                                        name="price_min"
                                        type="number"
                                        autoComplete="off"
                                        inputMode="numeric"
                                        value={priceMinText}
                                        onChange={(event) => setPriceMinText(event.target.value)}
                                        onBlur={() => commitPriceBound('min')}
                                        onKeyDown={(event) => handleInputKeyDown(event, () => commitPriceBound('min'))}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <Label htmlFor="price-max-input" className="text-xs text-gray-600">
                                        Máximo
                                    </Label>
                                    <Input
                                        id="price-max-input"
                                        name="price_max"
                                        type="number"
                                        autoComplete="off"
                                        inputMode="numeric"
                                        value={priceMaxText}
                                        onChange={(event) => setPriceMaxText(event.target.value)}
                                        onBlur={() => commitPriceBound('max')}
                                        onKeyDown={(event) => handleInputKeyDown(event, () => commitPriceBound('max'))}
                                    />
                                </div>
                            </div>
                        </FilterSection>

                        <FilterSection title="Horas de uso" expanded={expandedSections.hours} onToggle={() => toggleSection('hours')}>
                            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 tabular-nums">
                                {formatter.format(hoursPreview[0])}h - {formatter.format(hoursPreview[1])}h
                            </div>

                            <Slider
                                id="hours-range-slider"
                                aria-label="Intervalo de horas de uso"
                                min={hoursMin}
                                max={hoursMax}
                                step={100}
                                value={hoursPreview}
                                onValueChange={(value) => applyHoursRange(toRangeValue(value, hoursPreview))}
                                onValueCommit={(value) => applyHoursRange(toRangeValue(value, hoursPreview), true)}
                                className="py-2"
                            />

                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <Label htmlFor="hours-min-input" className="text-xs text-gray-600">
                                        Mínimo
                                    </Label>
                                    <Input
                                        id="hours-min-input"
                                        name="sailing_time_min"
                                        type="number"
                                        autoComplete="off"
                                        inputMode="numeric"
                                        value={hoursMinText}
                                        onChange={(event) => setHoursMinText(event.target.value)}
                                        onBlur={() => commitHoursBound('min')}
                                        onKeyDown={(event) => handleInputKeyDown(event, () => commitHoursBound('min'))}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <Label htmlFor="hours-max-input" className="text-xs text-gray-600">
                                        Máximo
                                    </Label>
                                    <Input
                                        id="hours-max-input"
                                        name="sailing_time_max"
                                        type="number"
                                        autoComplete="off"
                                        inputMode="numeric"
                                        value={hoursMaxText}
                                        onChange={(event) => setHoursMaxText(event.target.value)}
                                        onBlur={() => commitHoursBound('max')}
                                        onKeyDown={(event) => handleInputKeyDown(event, () => commitHoursBound('max'))}
                                    />
                                </div>
                            </div>
                        </FilterSection>

                        <FilterSection title="Tamanho (pés)" expanded={expandedSections.size} onToggle={() => toggleSection('size')}>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <Label htmlFor="size-min-input" className="text-xs text-gray-600">
                                        Mínimo
                                    </Label>
                                    <Input
                                        id="size-min-input"
                                        name="size_min"
                                        type="number"
                                        min={1}
                                        step="0.1"
                                        autoComplete="off"
                                        inputMode="decimal"
                                        placeholder="Ex.: 23"
                                        value={sizeMinText}
                                        onChange={(event) => setSizeMinText(event.target.value)}
                                        onBlur={commitSize}
                                        onKeyDown={(event) => handleInputKeyDown(event, commitSize)}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <Label htmlFor="size-max-input" className="text-xs text-gray-600">
                                        Máximo
                                    </Label>
                                    <Input
                                        id="size-max-input"
                                        name="size_max"
                                        type="number"
                                        min={1}
                                        step="0.1"
                                        autoComplete="off"
                                        inputMode="decimal"
                                        placeholder="Ex.: 29"
                                        value={sizeMaxText}
                                        onChange={(event) => setSizeMaxText(event.target.value)}
                                        onBlur={commitSize}
                                        onKeyDown={(event) => handleInputKeyDown(event, commitSize)}
                                    />
                                </div>
                            </div>
                        </FilterSection>

                        <FilterSection title="Motor" expanded={expandedSections.engine} onToggle={() => toggleSection('engine')}>
                            <div className="space-y-1">
                                <span className="text-xs text-gray-600">Combustível</span>
                                <div className="grid grid-cols-2 gap-2" role="group" aria-label="Combustível">
                                    {FUEL_TYPE_OPTIONS.map((fuel) => {
                                        const selected = (filters.fuelType ?? '').toLocaleLowerCase('pt-BR') === fuel.toLocaleLowerCase('pt-BR');

                                        return (
                                            <Button
                                                key={fuel}
                                                type="button"
                                                size="sm"
                                                variant={selected ? 'default' : 'outline'}
                                                aria-pressed={selected}
                                                onClick={() => onFiltersChange({ ...filters, fuelType: selected ? null : fuel })}
                                                className="px-2"
                                            >
                                                <span className="truncate">{fuel}</span>
                                            </Button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="space-y-1">
                                <Label htmlFor="engine-brand-input" className="text-xs text-gray-600">
                                    Marca do motor
                                </Label>
                                <Input
                                    id="engine-brand-input"
                                    name="engine_brand"
                                    type="text"
                                    autoComplete="off"
                                    maxLength={60}
                                    placeholder="Ex.: Mercury, Yamaha, Volvo"
                                    value={engineBrandText}
                                    onChange={(event) => setEngineBrandText(event.target.value)}
                                    onBlur={commitEngineBrand}
                                    onKeyDown={(event) => handleInputKeyDown(event, commitEngineBrand)}
                                />
                            </div>
                        </FilterSection>

                        <FilterSection title="Conforto" expanded={expandedSections.comfort} onToggle={() => toggleSection('comfort')}>
                            <div className="grid grid-cols-3 gap-2" role="group" aria-label="Conforto">
                                {(
                                    [
                                        ['cabin', 'Cabine'],
                                        ['bathroom', 'Banheiro'],
                                        ['generator', 'Gerador'],
                                    ] as const
                                ).map(([key, label]) => (
                                    <Button
                                        key={key}
                                        type="button"
                                        size="sm"
                                        variant={filters[key] ? 'default' : 'outline'}
                                        aria-pressed={filters[key]}
                                        onClick={() => toggleFlag(key)}
                                        className="px-2"
                                    >
                                        <span className="truncate">{label}</span>
                                    </Button>
                                ))}
                            </div>

                            <div className="space-y-1">
                                <span className="text-xs text-gray-600">Passageiros (mínimo)</span>
                                <div className="grid grid-cols-4 gap-2" role="group" aria-label="Passageiros mínimos">
                                    {PASSENGERS_MIN_OPTIONS.map((option) => {
                                        const selected = filters.passengersMin === option;

                                        return (
                                            <Button
                                                key={option}
                                                type="button"
                                                size="sm"
                                                variant={selected ? 'default' : 'outline'}
                                                aria-pressed={selected}
                                                onClick={() => onFiltersChange({ ...filters, passengersMin: selected ? null : option })}
                                                className="px-2"
                                            >
                                                {option}+
                                            </Button>
                                        );
                                    })}
                                </div>
                            </div>
                        </FilterSection>
                    </div>
                </div>

                {!alwaysExpanded && (
                    <div className="mt-4 flex gap-2 border-t border-gray-200 pt-4">
                        <Button type="button" variant="outline" className="flex-1" onClick={clearAllFilters} disabled={!hasActiveFilters}>
                            Limpar filtros
                        </Button>
                        {onCreateAlert && (
                            <Button type="button" onClick={onCreateAlert} className="flex-1 bg-[#00D26A] text-gray-900 hover:bg-[#00bd5f]">
                                <BellRing aria-hidden="true" className="h-4 w-4" />
                                Criar alerta
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
